// Onde desenhar o seletor de emojis.
//
// Ele era `position: absolute` dentro da mensagem, e a mensagem vive dentro de
// caixas com `overflow` escondido (a lista rolável e o painel da conversa).
// Qualquer transbordo era recortado: numa mensagem curta e encostada na lista
// de conversas, o seletor crescia para a esquerda e sumia atrás da coluna.
// Por isso ele passou a ser `position: fixed` na raiz da página, com a posição
// calculada aqui a partir do retângulo do botão de reagir.

export interface RetanguloDoBotao {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface PosicaoDoSeletor {
  left: number;
  top: number;
}

// Medidas do seletor: 6 emojis de 15px com respiro, na pílula com borda.
// Ficam aqui, e não no CSS, porque a posição precisa ser decidida antes de o
// seletor existir na tela.
export const TAMANHO_DO_SELETOR = { largura: 180, altura: 32 } as const;

/** Espaço entre o botão e o seletor, para não parecerem grudados. */
const FOLGA = 4;
/** Respiro mínimo até a borda da tela. */
const MARGEM = 8;

const limitar = (valor: number, minimo: number, maximo: number): number =>
  Math.max(minimo, Math.min(maximo, valor));

export function posicionarSeletor(
  botao: RetanguloDoBotao,
  viewport: Viewport,
  tamanho: { largura: number; altura: number } = TAMANHO_DO_SELETOR
): PosicaoDoSeletor {
  const { largura, altura } = tamanho;

  // Alinhado pela direita do botão: as ações da mensagem ficam à direita, e
  // crescer para a esquerda é o que mantém o seletor sobre a conversa.
  const left = limitar(
    botao.right - largura,
    MARGEM,
    Math.max(MARGEM, viewport.width - largura - MARGEM)
  );

  // Abaixo do botão por padrão, para não cobrir a mensagem; acima quando não
  // cabe (mensagem no pé da lista) — em telas baixas o limite final garante
  // que ele continue inteiro na janela.
  const abaixo = botao.bottom + FOLGA;
  const top =
    abaixo + altura <= viewport.height - MARGEM ? abaixo : botao.top - altura - FOLGA;

  return {
    left,
    top: limitar(top, MARGEM, Math.max(MARGEM, viewport.height - altura - MARGEM)),
  };
}
